// « Processus de l'agent » — the steps of one path, in order (owner, 2026-10-15, sketch).
// Drag a card to move it; ↑ / ↓ do the same by keyboard and on a phone. A 🔒 step
// never moves and never switches off. A step the order check refuses is red, with why.
import { useState } from 'react';
import { useT } from '@/lib/i18n';
import type { OrderError, PathKey, StepDef, StepSettings } from '@/lib/processApi';
import { reminderLine, SITE_KNOWN, type PathStep } from '@/lib/processOrder';

const WHO_FR: Record<StepDef['who'], string> = { agent: 'Agent', client: 'Client', owner: 'Vous', code: 'Code', courier: 'Coursier' };
const WHO_CLS: Record<StepDef['who'], string> = {
  agent: 'bg-blue-50 text-blue-800', client: 'bg-emerald-50 text-emerald-800', owner: 'bg-orange-50 text-orange-800',
  code: 'bg-gray-100 text-gray-700', courier: 'bg-gray-100 text-gray-700',
};


function Lock() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className="text-amber-800 shrink-0" role="img" aria-label="🔒">
      <rect x="5" y="11" width="14" height="10" rx="2" /><path d="M8 11V8a4 4 0 0 1 8 0v3" />
    </svg>
  );
}

export function StepList({ path, list, catalog, settings, errors, selected, onSelect, onMove, onToggle }: {
  path: PathKey;
  list: PathStep[];
  catalog: Map<string, StepDef>;
  settings: Record<string, StepSettings>;
  errors: OrderError[];
  selected: string | null;
  onSelect: (key: string) => void;
  onMove: (key: string, to: number) => void;
  onToggle: (key: string) => void;
}) {
  const { t } = useT();
  const [dragging, setDragging] = useState<string | null>(null);
  return (
    <ol className="flex flex-col gap-1.5" aria-label={t('Étapes dans l’ordre')}>
      {list.map((s, i) => {
        const def = catalog.get(s.key);
        if (!def) return null;
        const errs = errors.filter((e) => e.path === path && e.step === s.key);
        const set = settings[s.key];
        const skipped = path === 'site' && s.on && (set?.skipIf ?? []).length > 0 && (set?.skipIf ?? []).every((f) => SITE_KNOWN.has(f));
        const detail = [
          reminderLine(set?.reminderHours ?? [], t),
          skipped ? t('Ignorée : vient de Shopify') : (set?.skipIf?.length ? t('ignorée si déjà connu') : ''),
        ].filter(Boolean).join(' · ');
        const cls = errs.length
          ? 'border-2 border-red-600 bg-red-50'
          : selected === s.key ? 'border-2 border-adm-gold bg-amber-50/40' : skipped ? 'border border-dashed border-gray-300 bg-gray-50' : 'border border-gray-200 bg-white';
        return (
          <li
            key={s.key}
            draggable={!def.locked}
            onDragStart={(e) => { setDragging(s.key); e.dataTransfer.setData('text/plain', s.key); e.dataTransfer.effectAllowed = 'move'; }}
            onDragOver={(e) => { if (dragging) e.preventDefault(); }}
            onDrop={(e) => { e.preventDefault(); const k = e.dataTransfer.getData('text/plain') || dragging; if (k) onMove(k, i); setDragging(null); }}
            onDragEnd={() => setDragging(null)}
            className={`flex items-center gap-2 rounded-xl px-2 py-2 ${cls} ${s.on ? '' : 'opacity-60'} ${dragging === s.key ? 'ring-2 ring-adm-gold' : ''}`}
          >
            <span aria-hidden className={`w-5 flex justify-center ${def.locked ? 'text-gray-300 cursor-not-allowed' : 'text-gray-400 cursor-grab'}`}>⠿</span>
            <span className="w-6 text-right text-[12px] font-bold text-gray-500 tabular-nums">{i + 1}</span>
            <button type="button" onClick={() => onSelect(s.key)} className="flex-1 min-w-0 text-left cursor-pointer min-h-[40px]">
              <span className="flex items-center gap-2 flex-wrap">
                {def.locked && <Lock />}
                <span className={`text-[14px] font-bold ${errs.length ? 'text-red-800' : 'text-gray-900'}`}>{t(def.name)}</span>
                <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${WHO_CLS[def.who]}`}>{t(WHO_FR[def.who])}</span>
              </span>
              {detail && <span className="block text-[12px] text-gray-500 mt-0.5">{detail}</span>}
              {errs.map((e) => <span key={e.code + (e.mustFollow ?? '')} className="block text-[12px] font-semibold text-red-700 mt-1">{e.message}</span>)}
            </button>
            {!def.locked && (
              <span className="flex flex-col">
                <button type="button" aria-label={t('Monter « {x} »', { x: t(def.name) })} disabled={i <= 1} onClick={() => onMove(s.key, i - 1)}
                  className="w-9 h-5 text-[11px] text-gray-500 hover:text-gray-900 disabled:opacity-30 cursor-pointer">▲</button>
                <button type="button" aria-label={t('Descendre « {x} »', { x: t(def.name) })} disabled={i >= list.length - 1} onClick={() => onMove(s.key, i + 1)}
                  className="w-9 h-5 text-[11px] text-gray-500 hover:text-gray-900 disabled:opacity-30 cursor-pointer">▼</button>
              </span>
            )}
            {def.locked ? (
              <span className="shrink-0 text-[11.5px] font-bold px-2.5 py-1 rounded-full bg-amber-100 text-amber-900">{t('verrouillée')}</span>
            ) : (
              <button type="button" role="switch" aria-checked={s.on} aria-label={t('« {x} » activée', { x: t(def.name) })} onClick={() => onToggle(s.key)}
                className={`shrink-0 min-w-[48px] h-8 rounded-full text-[12px] font-bold cursor-pointer ${s.on ? 'bg-adm-gold text-[#2B2116] dark:text-[#2B2116]' : 'bg-gray-200 text-gray-600'}`}>
                {s.on ? 'ON' : 'OFF'}
              </button>
            )}
          </li>
        );
      })}
    </ol>
  );
}
